import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import CourseChrome from "../components/CourseChrome.js";
import { api, type Batch } from "../api.js";

interface RosterCourse {
  id: string;
  code: string;
  name: string;
  year: number;
  semester: number;
  archived: boolean;
}

interface RosterEntry {
  id: string;
  name: string;
  email: string;
  rollNumber: string | null;
  active: boolean;
  courseRole: string;
  batchId: string | null;
  batchName: string | null;
}

/**
 * Roster management. Because unknown emails are refused at login, nobody can
 * sign in until they appear here -- so CSV paste is the primary path, not a nicety.
 */
export default function Roster() {
  const { courseId } = useParams<{ courseId: string }>();
  const [course, setCourse] = useState<RosterCourse | null>(null);
  const [roster, setRoster] = useState<RosterEntry[]>([]);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [csv, setCsv] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    if (!courseId) return;
    api
      .get<{ course: RosterCourse; roster: RosterEntry[] }>(`/api/courses/${courseId}/roster`)
      .then((r) => {
        setCourse(r.course);
        setRoster(r.roster);
      })
      .catch((e) => setError(e.message));
    api
      .get<{ batches: Batch[] }>(`/api/courses/${courseId}/batches`)
      .then((r) => setBatches(r.batches))
      .catch(() => {});
  };

  useEffect(load, [courseId]);

  async function moveToBatch(userId: string, batchId: string) {
    if (!courseId) return;
    try {
      await api.patch(`/api/courses/${courseId}/roster/${userId}/batch`, {
        batchId: batchId || null,
      });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not change the batch.");
    }
  }

  async function importCsv() {
    if (!courseId) return;
    setBusy(true);
    setError(null);
    setStatus(null);

    const students: Array<{ email: string; name: string; rollNumber?: string; batch?: string }> = [];
    const bad: string[] = [];

    csv
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean)
      .forEach((line, i) => {
        // Accept "roll,name,email" in any column order by finding the field
        // that looks like an email -- teachers' spreadsheets are never uniform.
        const parts = line.split(",").map((p) => p.trim());
        const email = parts.find((p) => p.includes("@"));
        if (!email) {
          bad.push(`Line ${i + 1}: no email address`);
          return;
        }
        const rest = parts.filter((p) => p !== email);
        // Header rows slip in constantly; skip anything that says "email".
        if (line.toLowerCase().includes("e-mail") || parts.some((p) => p.toLowerCase() === "email")) return;

        const rollNumber = rest.find((p) => /^[A-Za-z]{0,4}\d{2,}[A-Za-z0-9]*$/.test(p));
        // A short token that is not the roll number and has no space is the
        // batch, e.g. "B1". Checked after roll number so "CS21B001" cannot be
        // mistaken for one.
        const batch = rest.find(
          (p) => p !== rollNumber && /^[A-Za-z]{0,3}[0-9]{1,2}$/.test(p) && p.length <= 4
        );

        students.push({
          email,
          name: rest.find((p) => /[a-zA-Z]{2,}\s/.test(p)) ?? rest[1] ?? rest[0] ?? email.split("@")[0],
          rollNumber,
          batch,
        });
      });

    if (students.length === 0) {
      setError(bad.length ? bad.join("; ") : "Nothing to import.");
      setBusy(false);
      return;
    }

    try {
      const r = await api.post<{ added: number; alreadyPresent: number }>(
        `/api/courses/${courseId}/roster`,
        { students }
      );
      setStatus(
        `Added ${r.added} student(s).` +
          (r.alreadyPresent ? ` ${r.alreadyPresent} were already enrolled.` : "") +
          (bad.length ? ` Skipped: ${bad.join("; ")}` : "")
      );
      setCsv("");
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Import failed.");
    } finally {
      setBusy(false);
    }
  }

  async function remove(userId: string) {
    if (!courseId || !confirm("Remove this student from the course?")) return;
    try {
      await api.del(`/api/courses/${courseId}/roster/${userId}`);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not remove the student.");
    }
  }

  return (
    <div className="page">
      <CourseChrome section="roster" course={course} />

      {error && <div className="error-banner">{error}</div>}
      {status && <div className="notice">{status}</div>}

      <div className="card">
        <div style={{ marginBottom: 8, color: "var(--muted)", fontSize: 12 }}>IMPORT STUDENTS</div>
        <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
          One student per line: roll number, name, email, and optionally a lab batch — in any order.
          Batches are created as they appear. Re-importing with a different batch moves the student,
          so this is also how you fix batch allocations in bulk. Students cannot sign in until they
          appear on this roster.
        </p>
        <textarea
          rows={6}
          placeholder={"CS21B004, Rahul Iyer, rahul@example.edu, B1\nCS21B005, Sneha Das, sneha@example.edu, B2"}
          value={csv}
          onChange={(e) => setCsv(e.target.value)}
        />
        <button className="primary" style={{ marginTop: 10 }} onClick={importCsv} disabled={busy || !csv.trim()}>
          {busy ? "Importing…" : "Import"}
        </button>
      </div>

      {batches.length > 0 && (
        <div className="card">
          <div style={{ marginBottom: 8, color: "var(--muted)", fontSize: 12 }}>LAB BATCHES</div>
          <div className="row" style={{ flexWrap: "wrap" }}>
            {batches.map((b) => (
              <div
                key={b.id}
                style={{
                  border: "1px solid var(--border)",
                  borderRadius: 6,
                  padding: "6px 12px",
                  background: "var(--bg)",
                }}
              >
                <strong>{b.name}</strong>{" "}
                <span className="muted">{b.studentCount} student{b.studentCount === 1 ? "" : "s"}</span>
                {b.scheduleNote && (
                  <div className="muted" style={{ fontSize: 12 }}>{b.scheduleNote}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card">
        <div className="between" style={{ marginBottom: 12 }}>
          <strong>Enrolled ({roster.length})</strong>
        </div>
        {roster.length === 0 ? (
          <div className="muted">Nobody enrolled yet.</div>
        ) : (
          <table>
            <thead>
              <tr><th>Roll no.</th><th>Name</th><th>Email</th><th>Batch</th><th>Role</th><th /></tr>
            </thead>
            <tbody>
              {roster.map((r) => (
                <tr key={r.id}>
                  <td className="muted">{r.rollNumber ?? "—"}</td>
                  <td>{r.name}</td>
                  <td className="muted">{r.email}</td>
                  <td>
                    {r.courseRole === "TEACHER" ? (
                      <span className="muted">—</span>
                    ) : (
                      <select
                        style={{ width: 120 }}
                        value={r.batchId ?? ""}
                        onChange={(e) => moveToBatch(r.id, e.target.value)}
                      >
                        <option value="">unassigned</option>
                        {batches.map((b) => (
                          <option key={b.id} value={b.id}>{b.name}</option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td>
                    <span className="badge draft">{r.courseRole.toLowerCase()}</span>
                  </td>
                  <td>
                    {r.courseRole !== "TEACHER" && (
                      <button className="danger" onClick={() => remove(r.id)}>Remove</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
